import { Extension, InputRule } from "@tiptap/core"
import { PluginKey } from "@tiptap/pm/state"
import Suggestion from "@tiptap/suggestion"

import type { EmojiSearchResult } from "@productlane/lib/src/emoji"
import {
	getEmojiForShortcodeName,
	searchEmojis,
} from "@productlane/lib/src/emoji"

import type { SuggestionOptions } from "@tiptap/suggestion"
import { emojiSuggestionRenderer } from "./suggestion"

export interface EmojiSuggestionCommandProps {
	emoji: string
}

export const EmojiSuggestionPluginKey = new PluginKey("emojiSuggestion")

// A single colon is too common in prose to open a popup, so wait for a query.
export const MIN_EMOJI_QUERY_LENGTH = 2

export const EMOJI_SUGGESTION_LIMIT = 8

const SHORTCODE_INPUT_RULE = /:([a-zA-Z0-9_+-]+):$/

export function getEmojiSuggestionItems(query: string): EmojiSearchResult[] {
	if (query.length < MIN_EMOJI_QUERY_LENGTH) return []
	return searchEmojis(query, EMOJI_SUGGESTION_LIMIT)
}

export interface EmojiSuggestionOptions {
	suggestion: Omit<
		SuggestionOptions<EmojiSearchResult, EmojiSuggestionCommandProps>,
		"editor"
	>
}

export const emojiSuggestionOptions: EmojiSuggestionOptions["suggestion"] = {
	char: ":",
	pluginKey: EmojiSuggestionPluginKey,
	allowSpaces: false,
	startOfLine: false,
	items: ({ query }) => getEmojiSuggestionItems(query),
	render: emojiSuggestionRenderer,
	command: ({ editor, range, props }) => {
		editor.chain().focus().insertContentAt(range, props.emoji).run()
	},
}

export const EmojiSuggestion = Extension.create<EmojiSuggestionOptions>({
	name: "emojiSuggestion",

	addOptions() {
		return { suggestion: emojiSuggestionOptions }
	},

	addInputRules() {
		return [
			new InputRule({
				find: SHORTCODE_INPUT_RULE,
				handler: ({ range, match, chain }) => {
					const name = match[1]
					if (!name) return
					const emoji = getEmojiForShortcodeName(name)
					if (!emoji) return
					chain().insertContentAt(range, emoji).run()
				},
			}),
		]
	},

	addProseMirrorPlugins() {
		return [
			Suggestion({
				editor: this.editor,
				...this.options.suggestion,
			}),
		]
	},
})
